import { emailImagesAPI } from '../api/endpoints';
import type { EmailImage } from '../types';

/**
 * The browser side of the email image library (docs/EMAIL_IMAGES.md).
 *
 * A stored mail body holds `<img src="cid:bmb-<id>">` and never the bytes. `cid:` is inert in a
 * browser, so the body has to be rewritten before anything renders it — and the image cannot be
 * fetched by pointing an `<img src>` at the API either: the access token lives in memory and goes
 * out as an `Authorization` header, which an `<img>` tag does not send.
 *
 * So each image is fetched once through axios and kept as a blob URL. The library is pre-warmed
 * at app start, which is what makes `blobForImage` **synchronous** — and that matters, because
 * `renderMailBody` sits on the contact overlay's render path and must not become async.
 *
 * Blob URLs live for the session and are never revoked: the count is bounded by the library cap,
 * and a revoked URL would blank out every body already rendered against it.
 */

/** `cid:bmb-<24 hex>` — deliberately narrow. A quoted reply chain carries `cid:` references of
 *  its own (Gmail writes `ii_…`), and only ours may be touched. */
const CID_RE = /cid:bmb-([0-9a-fA-F]{24})/g;

const blobUrls = new Map<string, string>();
/** blob URL → image id. The editor's document holds blob URLs (a `cid:` will not display), so
 *  this is how a body on its way to the server gets its references back. */
const idsByBlob = new Map<string, string>();
let library: EmailImage[] = [];
let warming: Promise<void> | null = null;
const readyListeners = new Set<() => void>();

/**
 * Called once the library's blob URLs exist.
 *
 * Anything that caches *rendered* bodies has to listen: a body rendered before the images
 * arrived cached the placeholder, and nothing else would ever invalidate it (the cache is keyed
 * by Gmail message id, and a stored body never changes).
 */
export function onEmailImagesReady(listener: () => void): () => void {
  readyListeners.add(listener);
  return () => readyListeners.delete(listener);
}

/** The `cid:` reference a library image is stored as. One definition, used by the editor too. */
export function cidForImage(id: string): string {
  return `cid:bmb-${id}`;
}

/** The blob URL for a library image, or null when it has not been fetched. */
export function blobForImage(id: string): string | null {
  return blobUrls.get(id.toLowerCase()) ?? null;
}

/** The library as last loaded. The picker reads this; it is empty until `prewarmEmailImages`. */
export function cachedEmailImages(): EmailImage[] {
  return library;
}

function rememberBlob(id: string, blobUrl: string): void {
  blobUrls.set(id.toLowerCase(), blobUrl);
  idsByBlob.set(blobUrl, id.toLowerCase());
}

async function fetchOne(image: EmailImage): Promise<void> {
  if (blobUrls.has(image.id.toLowerCase())) return;
  try {
    const { data } = await emailImagesAPI.getBytes(image.id);
    rememberBlob(image.id, URL.createObjectURL(data));
  } catch {
    // A missing image is not worth failing a render for: the body keeps its cid: reference and
    // `sanitizeHtml` turns it into the placeholder chip.
  }
}

/**
 * Load the library and every image's bytes. Safe to call repeatedly — concurrent calls share one
 * in-flight promise, and already-fetched images are skipped.
 *
 * Call this before anything that renders a mail body or opens the editor.
 */
export function prewarmEmailImages(): Promise<void> {
  if (warming) return warming;
  warming = (async () => {
    try {
      const { data } = await emailImagesAPI.list();
      library = data;
      await Promise.all(data.map(fetchOne));
      if (data.length) readyListeners.forEach((listener) => listener());
    } catch {
      // Not signed in, offline, or the route is unavailable — bodies render with placeholders
    } finally {
      warming = null;
    }
  })();
  return warming;
}

/** Add a just-uploaded image to the caches, so it is insertable without a reload. */
export function registerEmailImage(image: EmailImage, blobUrl?: string): void {
  library = [image, ...library.filter((existing) => existing.id !== image.id)];
  if (blobUrl) rememberBlob(image.id, blobUrl);
  else void fetchOne(image);
  readyListeners.forEach((listener) => listener());
}

/** Drop a deleted image from the picker. Its blob URL stays: history still references it. */
export function unregisterEmailImage(id: string): void {
  library = library.filter((image) => image.id !== id);
}

/**
 * Rewrite `cid:` references to blob URLs so the browser can display them.
 *
 * Used by both readers of a stored body: the history render (via `sanitizeHtml`) and the editor.
 * A reference with no blob — an image deleted, or the library not yet warm — is left as `cid:`,
 * which is inert and gets the placeholder treatment downstream.
 */
export function resolveImageSources(html: string): string {
  if (!html || !html.includes('cid:bmb-')) return html;
  return html.replace(CID_RE, (match, id: string) => blobForImage(id) ?? match);
}

/** Whether a body references any library image at all — a cheap guard before the regex work. */
export function hasImageReferences(html: string): boolean {
  return !!html && html.includes('cid:bmb-');
}

/**
 * A body on its way out of the editor, ready to be stored and sent.
 *
 * One rule decides every `<img>`: **it survives only if its source is a library image of this
 * user's.** A `cid:` reference passes through untouched (that is already the stored form, and one
 * whose blob never arrived must not be dropped just because the library was slow). A blob URL is
 * translated back. Everything else — an image pasted in from a web page or another mail, a
 * `data:` URI — is removed.
 *
 * That last clause is not tidiness. A hotlinked `<img>` would be broken for the recipient the day
 * it moves, blocked by default in most clients, and a tracking beacon when the pasted source was
 * a received mail; a `data:` URI is exactly the inlined payload this whole design exists to keep
 * out of the database.
 */
export function toStoredHtml(html: string): string {
  if (!html || !html.includes('<img')) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') ?? '';
    if (src.startsWith('cid:bmb-')) return;
    const id = idsByBlob.get(src);
    if (id) img.setAttribute('src', cidForImage(id));
    else img.remove();
  });
  return doc.body.innerHTML;
}

/**
 * A stored body on its way into the editor, where `src` has to be something the browser can load.
 *
 * The identity is not carried in a data attribute: the blob URL is itself the handle, and
 * `toStoredHtml` maps it back. That keeps the TipTap image node stock — no custom attribute to
 * preserve through every copy, paste and undo.
 */
export function toEditableHtml(html: string): string {
  return resolveImageSources(html);
}

/** Whether *html* holds any image at all — used where "empty body" is being decided. */
export function hasImage(html: string): boolean {
  return !!html && /<img\b/i.test(html);
}
